import { ReactNode, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import Background from "@/components/ui/Background";
import { H1 } from "@/components/ui/Typography";
import DataToggle from "@/components/common/DataToggle";
import MonthYearSelector from "@/components/common/MonthYearSelector";
import CurrencyToggle from "@/components/common/CurrencyToggle";
import { useCurrency } from "@/context/CurrencyContext";
import { notFoundUrl } from "@/routes/urls";
import { formatDateRange, FULL_YEAR_MONTHS } from "@/lib/time_utils";

type View = "overview" | "graph" | "monthly" | "raw";

interface FetchHook<T> {
  (
    clientId: string,
    connectionId: string,
    months: number[],
    year: number,
    enabled: boolean
  ): { data: T };
}

export interface FinancialPageConfig<Overview, Graph, Monthly, Raw> {
  title: string;
  showMonthly?: boolean;

  overviewHook: FetchHook<Overview>;
  graphHook?: FetchHook<Graph>;
  monthlyHook?: FetchHook<Monthly>;
  rawHook?: FetchHook<Raw>;

  OverviewComponent: React.FC<{
    data: Overview;
    startDate: string;
    endDate: string;
  }>;
  GraphComponent?: React.FC<{ data: Graph }>;
  MonthlyComponent?: React.FC<{
    data: Monthly;
    clientId: string;
    connectionId: string;
    monthsSelected: number[];
    year: number;
  }>;
  RawComponent?: React.FC<{ data: Raw }>;
}

function useDateFilter() {
  const [dateFilter, setDateFilter] = useState<{
    months: number[];
    year: number;
  }>({
    months: FULL_YEAR_MONTHS,
    year: new Date().getFullYear(),
  });

  const handleDateChange = ({
    months,
    year,
  }: {
    months: number[];
    year: number;
  }) => {
    setDateFilter({ months: months.length ? months : FULL_YEAR_MONTHS, year });
  };

  return { dateFilter, handleDateChange };
}

function FinancialPage<Overview, Graph, Monthly, Raw>({
  title,
  showMonthly = true,
  overviewHook,
  graphHook,
  monthlyHook,
  rawHook,
  OverviewComponent,
  GraphComponent,
  MonthlyComponent,
  RawComponent,
}: FinancialPageConfig<Overview, Graph, Monthly, Raw>): ReactNode {
  const { clientId, connectionId } = useParams<{
    clientId: string;
    connectionId: string;
  }>();
  const [view, setView] = useState<View>("overview");
  const { dateFilter, handleDateChange } = useDateFilter();
  const { unit, setUnit } = useCurrency();

  if (!clientId || !connectionId) {
    return <Navigate to={notFoundUrl} />;
  }

  const { data: overviewData } = overviewHook(
    clientId,
    connectionId,
    dateFilter.months,
    dateFilter.year,
    view === "overview"
  );

  const { data: graphData } = graphHook
    ? graphHook(
        clientId,
        connectionId,
        dateFilter.months,
        dateFilter.year,
        view === "graph"
      )
    : { data: undefined as unknown as Graph };

  const { data: monthlyData } = monthlyHook
    ? monthlyHook(
        clientId,
        connectionId,
        dateFilter.months,
        dateFilter.year,
        view === "monthly"
      )
    : { data: undefined as unknown as Monthly };

  const { data: rawData } = rawHook
    ? rawHook(
        clientId,
        connectionId,
        dateFilter.months,
        dateFilter.year,
        view === "raw"
      )
    : { data: undefined as unknown as Raw };

  const { startDateStr, endDateStr } = formatDateRange(
    dateFilter.months,
    dateFilter.year
  );

  return (
    <Background className="mx-auto px-4 py-8 flex flex-col min-h-screen">
      <div className="flex items-center justify-between mb-8 pl-2">
        <H1>{title}</H1>
      </div>

      <div className="relative flex items-center justify-between mb-2">
        <DataToggle
          active={view}
          onChange={setView}
          showMonthly={showMonthly}
        />

        <div className="absolute left-1/2 transform -translate-x-1/2">
          <CurrencyToggle active={unit} onChange={setUnit} />
        </div>

        <MonthYearSelector
          selectedMonths={dateFilter.months}
          selectedYear={dateFilter.year}
          onChange={handleDateChange}
        />
      </div>
      <>
        {view === "overview" && (
          <OverviewComponent
            data={overviewData as Overview}
            startDate={startDateStr}
            endDate={endDateStr}
          />
        )}
        {view === "graph" && GraphComponent && (
          <GraphComponent data={graphData as Graph} />
        )}
        {view === "monthly" && showMonthly && MonthlyComponent && (
          <MonthlyComponent
            data={monthlyData as Monthly}
            clientId={clientId}
            connectionId={connectionId}
            monthsSelected={dateFilter.months}
            year={dateFilter.year}
          />
        )}
        {view === "raw" && RawComponent && (
          <RawComponent data={rawData as Raw} />
        )}
      </>
    </Background>
  );
}

export default FinancialPage;
